import { UnassignedItem } from 'src/entities/dispatch';
import { PlanningsNotAllocReason } from 'src/entities/PlanningsNotAllocReasons.entity';

export type LaneSortField = 'quantity' | 'weight' | 'volume';
export type LaneSortOrder = 'asc' | 'desc';

export type LaneGroup = {
  /** 集約キー。名前空間付きなので lane / geo / addr / id が混ざっても衝突しない */
  key: string;
  items: UnassignedItem[];
  /** 配下の積地表示名（重複排除済み） */
  originLabels: string[];
  /** 配下の降地表示名（重複排除済み） */
  destinationLabels: string[];
  totalCases: number;
  totalWeight: number;
  totalVolume: number;
};

// 座標欠損の判定は adapter.ts:600 と同じ != null 系に揃える
// （entity 上は number だが実データには null が来る）。
function isCoord(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isFilled(value: string | undefined | null): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * レーンの同一性キー。
 *
 * geo / addr が「両端」を含むのが要点。UnassignedItem.geo は降地座標だけなので、
 * 降地単独をキーにすると「同じ降地へ向かう積地の違う案件」が誤って 1 レーンに合流する。
 * 名称キーは採らない（同名で住所の違う拠点が合流するため）。両端住所も無い場合は
 * id: キーにして集約しない（誤集約より非集約を選ぶ）。
 */
export function laneKeyOf(item: UnassignedItem): string {
  if (item.laneId != null) return `lane:${item.laneId}`;

  const source = item.source;
  if (source
    && isCoord(source.loading_lat) && isCoord(source.loading_lng)
    && isCoord(source.unloading_lat) && isCoord(source.unloading_lng)) {
    return `geo:${source.loading_lat},${source.loading_lng}->${source.unloading_lat},${source.unloading_lng}`;
  }

  if (isFilled(item.originAddress) && isFilled(item.storeAddress)) {
    return `addr:${item.originAddress}->${item.storeAddress}`;
  }

  return `id:${item.id}`;
}

/** 入力順（= フィルタ・検索後の可視リスト順）でレーンを作る。ソートは sortLaneGroups が行う。 */
export function buildLaneGroups(items: readonly UnassignedItem[]): LaneGroup[] {
  const byKey = new Map<string, LaneGroup>();

  items.forEach((item) => {
    const key = laneKeyOf(item);
    let group = byKey.get(key);
    if (!group) {
      group = { key, items: [], originLabels: [], destinationLabels: [], totalCases: 0, totalWeight: 0, totalVolume: 0 };
      byKey.set(key, group);
    }
    group.items.push(item);
    group.totalCases += item.cases ?? 0;
    group.totalWeight += item.weight ?? 0;
    group.totalVolume += item.volume ?? 0;

    const origin = item.loadSummary || item.origin;
    if (isFilled(origin) && !group.originLabels.includes(origin)) group.originLabels.push(origin);
    const destination = item.unloadSummary || item.storeName;
    if (isFilled(destination) && !group.destinationLabels.includes(destination)) group.destinationLabels.push(destination);
  });

  return [...byKey.values()];
}

export function sortLaneGroups(
  groups: readonly LaneGroup[],
  sortField: LaneSortField,
  sortOrder: LaneSortOrder,
): LaneGroup[] {
  const total = (group: LaneGroup) => {
    switch (sortField) {
      case 'quantity': return group.totalCases;
      case 'weight': return group.totalWeight;
      case 'volume': return group.totalVolume;
    }
  };

  // 合計同値のときキー文字列で決めるのは、昇順・降順のどちらでも描画順を固定するため
  // （第2キーを反転させると同値レーンの並びが向きで入れ替わって落ち着かない）。
  return [...groups].sort((a, b) => {
    const diff = total(a) - total(b);
    if (diff !== 0) return sortOrder === 'asc' ? diff : -diff;
    return a.key < b.key ? -1 : (a.key > b.key ? 1 : 0);
  });
}

/**
 * 配下伝票の未割当理由をマージして重複排除する。
 * 同じ車両・同じ文面の理由は複数伝票に付くので、delivery_id + message で一意にする
 * （order_id は伝票ごとに違うのでキーに含めない）。
 */
export function mergeLaneNotAllocReasons(
  items: readonly UnassignedItem[],
  notAllocReasonsByOrderId: ReadonlyMap<number, PlanningsNotAllocReason[]> | undefined,
): PlanningsNotAllocReason[] {
  if (!notAllocReasonsByOrderId) return [];
  const seen = new Set<string>();
  const merged: PlanningsNotAllocReason[] = [];
  items.forEach((item) => {
    const m = /^lr-(\d+)$/.exec(item.id);
    if (!m) return;
    const legRunId = parseInt(m[1], 10);
    (notAllocReasonsByOrderId.get(legRunId) ?? []).forEach((reason) => {
      const key = `${reason.delivery_id}::${reason.message}`;
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(reason);
    });
  });
  return merged;
}
